import { useEffect, useRef, useState } from "react";

import { DropdownField, InputField, View, Text, Tooltip, Icon } from "./base";
import { useAppSelector } from "../redux";
import { AgentEnvironmentVariable } from "../redux/reducers/agentEnvironmentVariableSlice";
import { ConfirmDialog } from "./ConfirmDialog";
import { IconButton } from "./base/IconButton";

type VariablesFormProps = {
  variables?: Partial<AgentEnvironmentVariable>[];
  onChange: (newVariable: Partial<AgentEnvironmentVariable>) => void;
  onCreate: (newVariable: { name: string; value: string; masked: boolean }) => void;
  onClickDelete: (id: string) => Promise<void>;
};

type DraftVariable = {
  draftId: string;
  name: string;
  value: string;
  masked: boolean;
  submitted: boolean;
};

let draftCounter = 0;

export const VariablesForm = ({ variables = [], onChange, onCreate, onClickDelete }: VariablesFormProps) => {
  const [internalVariables, setInternalVariables] = useState<Partial<AgentEnvironmentVariable>[]>(variables);
  const [draftVariables, setDraftVariables] = useState<DraftVariable[]>([]);
  const [isLoadingDeleteId, setIsLoadingDeleteId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingVariable, setDeletingVariable] = useState<Partial<AgentEnvironmentVariable> | null>(null);
  const isLoadingCreate = useAppSelector((state) => state.agentEnvironmentVariable.isLoadingCreate);

  const prevVariablesLengthRef = useRef(variables.length);

  const onChangeHandler = (id: string, key: string) => (value: string) => {
    const currentVariable = internalVariables.find((v) => v.id === id);
    const newVariable = { ...currentVariable, [key]: value };

    if (key === "masked") {
      newVariable.masked = value === "masked";
    }

    onChange(newVariable);
    setInternalVariables((prev) => prev.map((v) => (v.id === id ? newVariable : v)));
  };

  const onChangeDraftHandler = (draftId: string, key: string) => (value: string) => {
    setDraftVariables((prev) =>
      prev.map((d) => {
        if (d.draftId !== draftId || d.submitted) return d;
        if (key === "masked") {
          return { ...d, masked: value === "masked" };
        }
        return { ...d, [key]: value };
      }),
    );
  };

  const submitDraft = (draftId: string) => {
    const draft = draftVariables.find((d) => d.draftId === draftId);
    if (!draft || draft.submitted || !draft.name.trim() || !draft.value.trim()) return;

    setDraftVariables((prev) => prev.map((d) => (d.draftId === draftId ? { ...d, submitted: true } : d)));
    onCreate({ name: draft.name.trim(), value: draft.value.trim(), masked: draft.masked });
  };

  const handleDraftBlur = (draftId: string) => (e: React.FocusEvent<HTMLDivElement>) => {
    // Only submit if focus is leaving the entire draft row (not moving between fields within it)
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    submitDraft(draftId);
  };

  const handleDraftKeyDown = (draftId: string) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      submitDraft(draftId);
    }
  };

  const handleDeleteClick = (variable: Partial<AgentEnvironmentVariable>) => {
    setDeletingVariable(variable);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deletingVariable || !deletingVariable.id) return;

    setIsLoadingDeleteId(deletingVariable.id);
    setShowDeleteDialog(false);

    await onClickDelete(deletingVariable.id);

    setIsLoadingDeleteId(null);
    setDeletingVariable(null);
  };

  const onClickAdd = () => {
    setDraftVariables((prev) => [...prev, { draftId: `draft-${++draftCounter}`, name: "", value: "", masked: false, submitted: false }]);
  };

  const removeDraftVariable = (draftId: string) => {
    setDraftVariables((prev) => prev.filter((d) => d.draftId !== draftId));
  };

  useEffect(() => {
    if (variables.length !== internalVariables.length) {
      setInternalVariables(variables);
    }
  }, [variables]);

  // When a new variable arrives from the API, remove the corresponding submitted draft
  useEffect(() => {
    if (variables.length > prevVariablesLengthRef.current) {
      setDraftVariables((prev) => {
        const submittedDraft = prev.find((d) => d.submitted);
        if (submittedDraft) {
          return prev.filter((d) => d.draftId !== submittedDraft.draftId);
        }
        return prev;
      });
    }
    prevVariablesLengthRef.current = variables.length;
  }, [variables.length]);

  return (
    <View className="flex flex-col flex-1 w-full gap-2">
      {internalVariables
        .sort((a, b) => {
          const aDate = new Date(a.createdAt || 0).getTime();
          const bDate = new Date(b.createdAt || 0).getTime();

          return aDate - bDate;
        })
        .map((variable, index) => (
          <View
            key={index}
            className="flex w-full gap-2 items-center"
          >
            {variable.masked ? (
              <>
                <Tooltip
                  content="Masked variables cannot be edited."
                  className="w-full"
                >
                  <InputField
                    value={variable.name}
                    placeholder="Variable name"
                    isDisabled
                  />
                </Tooltip>
                <Tooltip
                  content="Masked variables cannot be edited."
                  className="w-full"
                >
                  <InputField
                    type="password"
                    value={variable.value}
                    placeholder="Variable value"
                    isDisabled
                  />
                </Tooltip>
                <Tooltip content="Masked variables cannot be edited.">
                  <DropdownField
                    value="masked"
                    className="!w-36 !min-w-36"
                    labelClassName="!w-36 !min-w-36"
                    isDisabled
                    options={[{ label: "Masked", value: "masked" }]}
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <InputField
                  value={variable.name}
                  onChange={onChangeHandler(variable.id || "", "name")}
                  placeholder="Variable name"
                />
                <InputField
                  type={variable.masked ? "password" : "text"}
                  value={variable.value}
                  onChange={onChangeHandler(variable.id || "", "value")}
                  placeholder="Variable value"
                />
                <DropdownField
                  value={variable.masked ? "masked" : "visible"}
                  onChange={onChangeHandler(variable.id || "", "masked")}
                  className="!w-36 !min-w-36"
                  labelClassName="!w-36 !min-w-36"
                  options={[
                    { label: "Visible", value: "visible" },
                    { label: "Masked", value: "masked" },
                  ]}
                />
              </>
            )}
            <IconButton
              variant="danger"
              icon="trash"
              onClick={() => handleDeleteClick(variable)}
              isLoading={isLoadingDeleteId === variable.id}
            />
          </View>
        ))}

      {draftVariables.map((draft) => (
        <div
          key={draft.draftId}
          onBlur={handleDraftBlur(draft.draftId)}
          className="contents"
        >
          <View className="flex w-full gap-2 items-center">
            <InputField
              value={draft.name}
              onChange={onChangeDraftHandler(draft.draftId, "name")}
              onKeyDown={handleDraftKeyDown(draft.draftId)}
              placeholder="Variable name"
              isDisabled={draft.submitted}
            />
            <InputField
              type={draft.masked ? "password" : "text"}
              value={draft.value}
              onChange={onChangeDraftHandler(draft.draftId, "value")}
              onKeyDown={handleDraftKeyDown(draft.draftId)}
              placeholder="Variable value"
              isDisabled={draft.submitted}
            />
            <DropdownField
              value={draft.masked ? "masked" : "visible"}
              onChange={onChangeDraftHandler(draft.draftId, "masked")}
              className="!w-36 !min-w-36"
              labelClassName="!w-36 !min-w-36"
              isDisabled={draft.submitted}
              options={[
                { label: "Visible", value: "visible" },
                { label: "Masked", value: "masked" },
              ]}
            />
            <IconButton
              variant="danger"
              icon="trash"
              onClick={() => removeDraftVariable(draft.draftId)}
              isLoading={draft.submitted}
            />
          </View>
        </div>
      ))}

      <button
        onClick={onClickAdd}
        disabled={isLoadingCreate}
        className="flex flex-row items-center gap-2 py-2 w-full text-left hover:opacity-80 transition-opacity group disabled:opacity-50"
      >
        <Icon
          name="add"
          size={14}
          className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
        />
        <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
          Add variable
        </Text>
      </button>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeletingVariable(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Variable"
        message={`Are you sure you want to delete the variable "${deletingVariable?.name}"?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone. The variable will be permanently removed.</Text>
      </ConfirmDialog>
    </View>
  );
};
